/**
 * FocusExplorer — the focused module and everything it is wired to, as cards
 * in the studio's phone strip.
 *
 * It renders a FRAGMENT, not a container: its cards are siblings of the live
 * preview inside the pane's one horizontal strip, so every item is the same
 * `PhoneCard` at the same size, top-aligned, with no nested zones left to
 * drift apart. The strip reads:
 *
 *   [ preview (the pane's, live) ] [ data ] [ FOCUS ] [ connection 1 ] [ 2 ] …
 *
 * Each run carries a TITLE in the band above the cards — Preview · Data · Focus
 * · Connections — so the zones can be named out loud instead of pointed at.
 *
 *   FOCUS        the focused module's top-down view — its page rendered with
 *                the module and its ancestors normal, its siblings grayed and
 *                its own box outlined. With nothing focused it shows the live
 *                app so there is something to pick from. It is also the
 *                SELECTION surface: an inspect overlay swallows the tap and
 *                reports the blueprint id under it (`elementsFromPoint` → the
 *                nearest `[data-vi-node]` marker), so no host action fires;
 *                tapping the focused module again releases it.
 *   connections  one card per connection the focus takes part in — the module
 *                at the OTHER end, on ITS own page (often a different one).
 *                When that other end is the HOST, the card draws the host's
 *                DESTINATION PAGE if the slot opens one (`openItem` → the page
 *                bound to `focusEntry`, `openSession` → the one bound to
 *                `focusGrid`); a host slot with no page of ours behind it —
 *                `openCapture`, `crudRemove`, `imageTap` — keeps the bridge
 *                chip. "Tap a card and the app opens the detail page" is half
 *                the interaction, and a chip drew none of it.
 *
 * One connection is ACTIVE: it leads the run of connection cards and a
 * labelled wire runs between the two MODULE boxes, pointed the way the data
 * actually travels (`dir`), solid for sync and dashed for async. Clicking any
 * other card makes it active and the wire moves. The wire is an overlay inside
 * the strip's SCROLL CONTENT, so it scrolls with the cards and is painted
 * above them — nothing can clip or occlude it.
 *
 * Every card renders over the PANE's feed and the PANE's interaction store,
 * never one of its own. That is what makes the strip one app: the lifecycle
 * states the admin injected show here too, and a filter tapped on the live card
 * filters the identical grid node in the focus card (`slotState` is keyed by
 * node id, so a grid on another page is untouched — correctly).
 */
import { useCallback, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import NativeWidgetRuntime from '../NativeWidgetRuntime';
import { mockFeed } from '../mock';
import { focusViews, defaultFocus, hostOpensPage, hostPageArgs, declaredOwner } from './focusModel';
import { ZONE_GAP, ZONE_LABEL_H } from './phoneMetrics';
import InspectLayer from './InspectLayer';
import PhoneCard, { CARD_H } from './PhoneCard';
import DataModelCard from './DataModelCard';
import SessionCard from './SessionCard';
import { sessionNode, SESSION_SLOTS } from './sessionModel';
import { readersOf, dataEndpoints, feederRows } from './dataTree';
import { scrollNodeIntoView } from './scrollWithin';
import useUserFeed from './useUserFeed';
import { bootstrapHref } from './studioNav';

const C = {
  text: '#1c1c1e', dim: '#6e6e73', accent: '#6E59F2',
  ui: '#5856d6', data: '#b45309', host: '#6e6e73',
};

/**
 * A module's component type, as a link into the Component Bootstrap filtered to
 * it — with its library level (L0–L4) so you can read "which layer is this?" at
 * a glance. Lives in the caption block, outside the inspect overlay, so the tap
 * lands here and not on the selection surface.
 */
function TypeBadge({ type, level, onJump }) {
  if (!type) return null;
  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onJump(type); }}
      title={`Show ${type} in the Component Bootstrap`}
      style={{
        font: 'inherit', color: C.accent, background: 'none', border: 'none',
        padding: 0, cursor: 'pointer', textDecoration: 'underline', textUnderlineOffset: 2,
      }}
    >{type}{level ? ` (${level})` : ''}</button>
  );
}

/**
 * A card's title as TWO ROWS. Row 1 is the node id on ONE line — a laid-out
 * card's id carries a uuid (`shelves__card__027f397b-…`) and will never fit, so
 * it ellipsises and keeps the full value in its tooltip. Row 2 is the component
 * TYPE, which must never be cut: it is the answer to "what IS this", and when
 * both shared a 2-line clamp the id ate the type entirely.
 */
function CardTitle({ id, type, level = null, onJump = null }) {
  return (
    <>
      <span
        title={id}
        style={{ display: 'block', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
      >{id}</span>
      {type ? (
        <span style={{ display: 'block', minWidth: 0, wordBreak: 'break-word' }}>
          {onJump ? <TypeBadge type={type} level={level} onJump={onJump} /> : type}
        </span>
      ) : null}
    </>
  );
}

/** My-data with nothing stored: the widget's own empty state, never mock. */
const EMPTY_FEED = { entries: [], settings: {}, pending: { adds: [], updates: [] } };

// Each card is a REAL page render. A hub module (`host` on my-wardrobe wires
// 20+) would otherwise mount twenty pages at once, so only the front of the
// run renders; the rest wait until they are clicked to the front.
const RENDERED = 6;

/** Nothing to gray: a host-opened page is drawn whole, not as a frontier. */
const NO_DIM = new Set();

const laneColor = (executor) => C[executor === 'data' ? 'data' : executor === 'host' ? 'host' : 'ui'];

/**
 * The rule between two zones. It lives in the gap column and keeps the same
 * ZONE_GAP width, so adding it moved nothing — the wire's anchors are
 * unchanged. SOLID marks the boundary between the live app and the diagram;
 * DASHED separates the diagram's own zones from each other.
 */
function Separator({ dashed = false }) {
  return (
    <div
      aria-hidden="true"
      data-zone-gap="1"
      data-separator={dashed ? 'dashed' : 'solid'}
      style={{
        flex: `0 0 ${ZONE_GAP}px`, display: 'flex', justifyContent: 'center',
        paddingTop: ZONE_LABEL_H, // the zone band, so the rule spans the FRAMES
      }}
    >
      <div style={{
        width: 0, height: CARD_H, alignSelf: 'flex-start',
        borderLeft: `1px ${dashed ? 'dashed' : 'solid'} rgba(0,0,0,.14)`,
      }} />
    </div>
  );
}

/**
 * The dim + box rules. Both reach the marker's CHILDREN: the marker itself is
 * `display: contents` and paints nothing.
 */
function InspectStyles() {
  return (
    <style>{`
.vi-focus-mark [data-vi-dim="1"] > * { opacity: .16; filter: grayscale(1); }
/* The box is an OVERLAY, not an outline. Two constraints fight here: the module fills
   its scroller exactly and that scroller is overflow:hidden, so an OUTSET outline is
   clipped away on all four sides (invisible) — but an INSET one (outline-offset:-3px)
   paints UNDER the module's own children, so a card's art and text cover its edges and
   the box reads as a broken rectangle. A pseudo-element inset by 1px is inside the clip
   zone AND above the content, so the rectangle is always complete. z-index stays below
   the wires (50); pointer-events:none keeps the inspect tap reaching the overlay. */
.vi-focus-mark [data-vi-box="1"] > * { position: relative; }
.vi-focus-mark [data-vi-box="1"] > *::after {
  content: ''; position: absolute; inset: 1px;
  border: 2px solid ${C.accent}; border-radius: 8px;
  pointer-events: none; z-index: 40;
}
`}</style>
  );
}

/**
 * The rendered box of one module in the strip's SCROLL-CONTENT coordinates —
 * the union of its marker's children (the marker has no box of its own),
 * clamped to the phone frame. A module taller than the phone is CLIPPED by the
 * frame, and a wire aimed at its true centre would point outside the card and
 * read as broken.
 */
function moduleRect(strip, frame, nodeId) {
  if (!strip || !frame) return null;
  const origin = strip.getBoundingClientRect();
  const box = frame.getBoundingClientRect();
  const esc = nodeId && (globalThis.CSS?.escape ? globalThis.CSS.escape(nodeId) : nodeId);
  const marker = esc ? frame.querySelector(`[data-vi-node="${esc}"]`) : null;
  let hit = null;
  for (const kid of marker ? marker.children : []) {
    const r = kid.getBoundingClientRect();
    if (!r.width && !r.height) continue;
    hit = hit ? {
      left: Math.min(hit.left, r.left), top: Math.min(hit.top, r.top),
      right: Math.max(hit.right, r.right), bottom: Math.max(hit.bottom, r.bottom),
    } : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
  }
  const src = hit || box;
  const dx = strip.scrollLeft - origin.left;
  const dy = strip.scrollTop - origin.top;
  return {
    left: Math.max(src.left, box.left) + dx,
    right: Math.min(src.right, box.right) + dx,
    top: Math.min(Math.max(src.top, box.top), box.bottom) + dy,
    bottom: Math.max(Math.min(src.bottom, box.bottom), box.top) + dy,
  };
}

/**
 * Any element's box in the strip's scroll-content coordinates, clamped to
 * `clip` when one is given — the same rule `moduleRect` applies to the phone
 * frame, for the same reason: the data card's body SCROLLS, and a selector row
 * below its fold is clipped away, so a wire aimed at that row's true position
 * would start in empty space under the card and read as broken.
 */
function rectIn(strip, el, clip = null) {
  const origin = strip.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  const box = clip?.getBoundingClientRect() || null;
  const dx = strip.scrollLeft - origin.left;
  const dy = strip.scrollTop - origin.top;
  if (!box) return { left: r.left + dx, right: r.right + dx, top: r.top + dy, bottom: r.bottom + dy };
  return {
    left: Math.max(r.left, box.left) + dx,
    right: Math.min(r.right, box.right) + dx,
    top: Math.min(Math.max(r.top, box.top), box.bottom) + dy,
    bottom: Math.max(Math.min(r.bottom, box.bottom), box.top) + dy,
  };
}

/**
 * One tree ROW by its node id, inside the data card. Every row in both columns
 * carries `data-tree-node`, so this is the whole of "find the wire's data end".
 * The id is quoted rather than escaped — it carries `:` and `#`, both legal
 * inside an attribute-selector string, and neither `"` nor `\` can appear in
 * one (they are built from identity fields and indices).
 */
function rowNode(card, id) {
  if (!card || !id) return null;
  return card.querySelector(`[data-tree-node="${String(id).replace(/["\\]/g, '\\$&')}"]`);
}

/**
 * A DATA-ZONE row's box, clamped to ITS OWN COLUMN — the two columns scroll
 * independently, so a row below the fold has to anchor at the column's edge the
 * way a module below the phone's fold anchors at the frame's. Without the clamp
 * a wire to `sel:medsStatus:item:…` starts in empty space under the card.
 */
function rowRect(strip, card, id) {
  const el = rowNode(card, id);
  return el ? rectIn(strip, el, el.closest('[data-vi-scroll]')) : null;
}

/**
 * WHICH EDGES TWO BOXES MEET ON. Left-to-right is the normal reading (data zone
 * → focus zone, stored column → derived column), but a wire between two rows of
 * the SAME column has no side to cross, so it leaves and lands on the right and
 * bows out into the gutter rather than doubling back through the text.
 */
function anchorPair(A, B) {
  const midY = (r) => (r.top + r.bottom) / 2;
  if (B.left >= A.right) return { x1: A.right, y1: midY(A), x2: B.left, y2: midY(B) };
  if (B.right <= A.left) return { x1: A.left, y1: midY(A), x2: B.right, y2: midY(B) };
  return { x1: A.right, y1: midY(A), x2: B.right, y2: midY(B) };
}

export default function FocusExplorer({
  blueprint, widgetId, appearance = 'light',
  focus = null, onFocus, page = null, registry = null,
  dataMode = 'mock', instanceId = null, feed: feedProp = null, interaction = null,
  pageArgs = null,
}) {
  const navigate = useNavigate();
  // type → library level (L0–L4), for the clickable type badge. Keyed by both
  // the registry id (what a blueprint's `type` is) and the human name.
  const levelOf = useMemo(() => {
    const m = new Map();
    for (const c of registry?.components || []) { m.set(c.id, c.layer); if (c.name) m.set(c.name, c.layer); }
    return (t) => m.get(t) || null;
  }, [registry]);
  const jumpTo = useCallback((type) => navigate(bootstrapHref(type)), [navigate]);
  // Which connection owns the wire. Keyed BY the focus it belongs to, so a new
  // focus starts at its first card without an effect.
  const [picked, setPicked] = useState({ focus: null, index: 0 });

  const views = useMemo(() => focusViews(blueprint, focus), [blueprint, focus]);
  const self = focus ? views[0] : null;
  // A runtime collection cell is not itself declared in the blueprint. Name
  // both halves in the caption: what rendered (`Card` / `PendingCard`) and the
  // authored collection that owns its layout, data and public ports.
  const ownerId = focus ? declaredOwner(blueprint, focus) : null;
  const ownerType = ownerId && ownerId !== focus ? blueprint?.components?.[ownerId]?.type || null : null;
  // The PAGE is its own state, never derived from the focus: releasing a focus
  // must leave you where you are, not snap back to the entry page.
  const shownPage = page || self?.page?.id || null;
  const connections = useMemo(() => (focus ? views.slice(1) : []), [focus, views]);
  const active = picked.focus === focus ? Math.min(picked.index, Math.max(0, connections.length - 1)) : 0;
  // ONE feed for the whole strip. In My-data mode every card renders the
  // user's real rows — a card falling back to `mockFeed` would put six mock
  // medicines beside a preview showing three real ones. Empty stays empty: the
  // widget's own first-run state is the honest answer.
  //
  // In mock mode the pane's OWN feed wins when it has one: it is the base mock
  // plus whatever the admin injected (the state chips) and added (the capture button), so
  // a "Running" placeholder shows here too rather than only on the live card.
  // Its `null` means "nothing injected", which is the clean base mock.
  const live = useUserFeed(dataMode, widgetId, instanceId);
  const mock = useMemo(() => mockFeed(widgetId), [widgetId]);
  const feed = dataMode === 'user' ? (live.feed || EMPTY_FEED) : (feedProp || mock);
  const liveInspect = useMemo(() => ({ dim: new Set() }), []);
  const hint = useMemo(() => defaultFocus(blueprint), [blueprint]);

  // The active connection LEADS the run, so the wire always lands on the first
  // card and the reading order matches the wire.
  const ordered = useMemo(() => (connections.length
    ? [connections[active], ...connections.filter((_, i) => i !== active)]
    : []), [connections, active]);
  const wired = ordered[0] || null;
  const wiredId = useMemo(() => wired?.nodeId || null, [wired]);

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const dataRef = useRef(null);
  const dataRowRef = useRef(null);
  const [wire, setWire] = useState(null);
  // EVERY data wire at once, measured in one pass — see `measureData`. The
  // endpoints are resolved there and nowhere else, so what is boxed below and
  // what the wires point at are the same list read twice.
  const [dataWires, setDataWires] = useState(null);
  // WHAT THE DATA ZONE SAYS, as one report: the current selection (a user pick,
  // or the row a focused card reverse-maps to), the user PICK on its own, the
  // FOCUSED CARD's own row (which is not the same thing — see `openArgs`), the
  // evaluated tree those row ids belong to, and — with no pick — the rows that
  // FEED the focus. The left wires are drawn from exactly this and nothing else.
  const [dataZone, setDataZone] = useState({ sel: null, pick: null, cardRow: null, tree: null, converge: [] });
  const dataPick = dataZone.sel;
  // A focused CARD is a focused ROW: its id is `<grid>__card__<itemId>`, so the
  // item id rides in the focus itself and the data zone can open onto it.
  const focusItemId = useMemo(() => {
    const i = String(focus || '').indexOf('__card__');
    return i < 0 ? null : String(focus).slice(i + '__card__'.length) || null;
  }, [focus]);
  // …and the modules at the OTHER end of those wires. This is the reverse
  // direction of `selectorsRead`: the picked node belongs to a selector, and
  // every module that binds that selector renders it.
  const readers = useMemo(() => readersOf(blueprint), [blueprint]);

  /**
   * A module tapped in the focus card, a `→ module` badge, the data footer's
   * `rendered by`. It lights the data zone (the pick is keyed by focus, so it
   * is released) and nothing else — a focus click must never write a pick, or
   * the two would take turns setting each other.
   */
  const focusFromZone = useCallback((id) => { onFocus?.(id); }, [onFocus]);

  /** Is this tree row on screen? A row inside a collapsed node is not. */
  const hasRow = useCallback((id) => Boolean(rowNode(dataRef.current, id)), []);

  /**
   * A DATA PICK LANDS THE FOCUS ON ITS FIRST ENDPOINT — resolved here, in the
   * click, because this is where the committed DOM is: `dataEndpoints` asks the
   * render which cards a grid actually laid out and asks the data card which
   * rows it is actually showing.
   *
   * DETERMINISTIC, and that is what keeps the old bias out: "first" is the
   * fan's own order (grid-laying modules lead), never "the one that is focused".
   * The WIRE SET is computed with no focus term at all — the selection follows
   * the set, the set never follows the selection. A row that reaches no
   * focus-zone node at all (its only surface is a derived row) clears the focus
   * rather than leaving the reader boxed on something unrelated.
   */
  const takePick = useCallback((next, tree) => {
    const ends = dataEndpoints(blueprint, readers, next, fromRef.current, {
      tree, hasRow, page: shownPage, pageArgs,
    });
    const first = ends.find((e) => e.zone === 'focus') || null;
    const id = first ? first.target : null;
    onFocus?.(id);
    return id;
  }, [blueprint, readers, hasRow, onFocus, shownPage, pageArgs]);

  /**
   * `page_args` for a page the HOST opens off this connection.
   *
   * IT IS THE FOCUSED CARD'S ROW, because this card is a PROMISE: "tap that
   * card and the app opens this". The live tap carries the card's own item, so
   * anything else here is the studio showing one thing and the app opening
   * another — which is what happened. The data zone reports two rows and they
   * are not the same row: `sel` is what the READER last clicked, `cardRow` is
   * what the focused card IS. They agree while the pick came from the card, and
   * diverge the moment a pick lands on a card that folds several rows — pick
   * scene-studio's still and the focus lands on `…__card__s-heavenly`, whose
   * rail card is the CLIP. Grounding on `sel` then previewed the still (Image,
   * no duration) beside a card that opens the video.
   *
   * The ROW, not the key, because the row knows every spelling: a
   * `SessionCollection` keys its cards by the SESSION, and an item page opened
   * on that key asks for a creation id the row does not answer to under that
   * name (`hostPageArgs`). With no card focused there is nothing to promise
   * about, so the reader's own pick is the honest subject — a module focus with
   * a picked row draws that row's destination.
   */
  const openArgs = useCallback(
    (argKey) => hostPageArgs(argKey, (focusItemId ? dataZone.cardRow : dataPick?.row) || null, focusItemId),
    [dataPick, dataZone.cardRow, focusItemId],
  );
  /**
   * THE SESSION a host connection opens, or null when the slot opens none.
   *
   * Four host slots leave the widget with a PAYLOAD — a prompt, its hidden
   * instructions, the capture framing, the session it lands in — and the strip
   * drew all four as the same dead chip. It resolves against the page the
   * FOCUS is on (that is where the emitter lives, and where `focusEntry.*`
   * either resolves or honestly does not), and reads the option the user
   * clicked out of the shared interaction store, since a template rail is one
   * node and eleven different payloads.
   */
  const sessionFor = useCallback((v) => (
    v?.kind === 'host' && SESSION_SLOTS.has(v.slot)
      ? sessionNode({
        blueprint,
        feed,
        pageArgs,
        conn: { payload: v.payload ?? null },
        slot: v.slot,
        emitterId: v.emitter,
        uiSelected: interaction?.ui?.[v.emitter],
        pageId: shownPage,
      })
      : null
  ), [blueprint, feed, pageArgs, shownPage, interaction]);

  /** A page's root component id — what a host-opened page card boxes. */
  const pageRootOf = useCallback(
    (pageId) => (blueprint?.pages || []).find((p) => p.id === pageId)?.root || null,
    [blueprint],
  );
  // EVERY FOCUS-ZONE node a wire touches, as a set of node ids — the box side
  // of the same list the wires are drawn from, plus the focus itself when there
  // is one (focusing a card must box it whether or not any row picks it out).
  // A wire that ends on a DATA row boxes nothing: there is no card to outline,
  // and the row is already marked in the column it lives in.
  // Keyed by the target STRING so the Set — and the `inspect` it goes into —
  // only changes identity when the endpoints really do.
  const focusTargets = (dataWires?.lines || []).filter((l) => l.zone === 'focus');
  const boxKey = focusTargets.map((l) => l.target).join('|');
  const boxedIds = useMemo(
    () => new Set([...(boxKey ? boxKey.split('|') : []), ...(focus ? [focus] : [])]),
    [boxKey, focus],
  );
  // WHERE TO SCROLL, which is a different question from what to outline: the
  // focus is what the reader just asked for, and with none it is the leading
  // endpoint (grid first, so the primary card). A string, never the set — an
  // effect keyed on a fresh Set would re-run on every measure.
  const revealId = focus || (focusTargets[0]?.target ?? null);
  // …and the same endpoints as MODULES, for the data zone's `rendered by`. It
  // reads the drawn wires rather than resolving the pick a second time, which
  // is what keeps the footer's list and the wires the same statement.
  const renderers = useMemo(
    () => [...new Set((dataWires?.lines || []).map((l) => l.module).filter(Boolean))],
    [dataWires],
  );

  /** Anchor the wire between the two MODULE boxes, in strip content space. */
  const measure = useCallback(() => {
    // Reach the scroller through the DOM, not a ref from the parent: the
    // parent's ref is attached AFTER this (a descendant) runs its layout
    // effect, so on first paint that ref is still null.
    const strip = fromRef.current?.closest('[data-strip]');
    if (!strip || !toRef.current) { setWire(null); return; }
    const A = moduleRect(strip, fromRef.current, focus);
    const B = moduleRect(strip, toRef.current, wiredId);
    if (!A || !B) { setWire(null); return; }
    setWire({
      w: strip.scrollWidth, h: strip.scrollHeight,
      x1: A.right, y1: (A.top + A.bottom) / 2,
      x2: B.left, y2: (B.top + B.bottom) / 2,
    });
  }, [focus, wiredId]);

  /**
   * THE LEFT WIRES, and there are two readings of them — they point OPPOSITE
   * ways, and which way is the whole difference between the two gestures:
   *
   *   a DATA pick FANS OUT.  One start (the picked row) and one wire per place
   *     that row is displayed: the card the render laid out for it, or — where
   *     no card exists — the last derived row it survives to (`dataEndpoints`).
   *     A derived pick also draws BACKWARD, from the stored writes it folded
   *     from, so the row sits mid-pipeline with its origins on one side and its
   *     displays on the other.
   *   a FOCUS click CONVERGES.  Every wire ENDS on the focused node and starts
   *     at a row that feeds it, at the focus's own level (`feedsOfFocus`). It
   *     used to pivot: focusing a card auto-picked its row and re-ran the fan,
   *     so focusing ONE clip drew wires to its SIBLING cards — an answer to a
   *     question nobody asked. Nothing pivots now; the focus is a terminus.
   *
   * ON A DETAIL PAGE both readings run through its BOUND ITEM — the row
   * `page_args` opened the page on — which is why `shownPage` and `pageArgs`
   * are inputs here. A module that reads `focusEntry.*` converges from that
   * row's rows, and a pick that is NOT that row reaches nothing on this page
   * and draws nothing: the page lays out no collection, so the outward fan used
   * to fall through to derived rows and spray wires into the data column for
   * items the page is not even showing.
   *
   * BOTH ends are resolved HERE, against the committed DOM, because both need
   * it: the render is what knows which cards a grid actually laid out, and the
   * data card is what knows which rows are not folded away inside a closed node.
   * One pass, one array — the boxes above are this array's focus-zone targets,
   * so the wire's far end and the outlined card can never disagree.
   */
  const measureData = useCallback(() => {
    const strip = fromRef.current?.closest('[data-strip]');
    const card = dataRef.current;
    if (!strip || !card) { setDataWires(null); return; }
    const { pick, tree, converge } = dataZone;
    const owner = focus ? declaredOwner(blueprint, focus) || focus : null;

    // [{ from: <row id>, to: {zone,id}, label, module }] — the model, before
    // anything is measured. `from` is always a DATA row: both readings start in
    // the data zone, and only the far end changes zone.
    const specs = [];
    if (pick) {
      for (const f of feederRows(blueprint, tree, pick)) {
        specs.push({ from: f.id, to: { zone: 'data', id: pick.id }, label: `${f.stage} → ${pick.label}` });
      }
      for (const ep of dataEndpoints(blueprint, readers, pick, fromRef.current, {
        tree, hasRow, page: shownPage, pageArgs,
      })) {
        specs.push({
          from: pick.id,
          to: { zone: ep.zone, id: ep.target },
          label: ep.label,
          module: ep.module,
        });
      }
    } else if (focus) {
      for (const r of converge || []) {
        specs.push({ from: r.id, to: { zone: 'focus', id: focus }, label: `reads ${r.stage}`, module: owner });
      }
    }

    const rectOf = (end) => (end.zone === 'focus'
      ? moduleRect(strip, fromRef.current, end.id)
      : rowRect(strip, card, end.id));
    const lines = [];
    for (const s of specs) {
      // the picked row is where `rowRef` sits, so it is found even mid-reveal
      const A = s.from === pick?.id
        ? (dataRowRef.current ? rectIn(strip, dataRowRef.current, dataRowRef.current.closest('[data-vi-scroll]')) : rowRect(strip, card, s.from))
        : rowRect(strip, card, s.from);
      const B = A ? rectOf(s.to) : null;
      if (!A || !B) continue;
      lines.push({
        key: `${s.from}|${s.to.id}`,
        from: s.from,
        target: s.to.id,
        zone: s.to.zone,
        module: s.module ?? null,
        label: s.label,
        ...anchorPair(A, B),
      });
    }
    if (!lines.length) { setDataWires(null); return; }
    setDataWires({ w: strip.scrollWidth, h: strip.scrollHeight, lines });
  }, [blueprint, readers, focus, hasRow, dataZone, shownPage, pageArgs]);

  useLayoutEffect(() => {
    measure();
    measureData();
    const strip = fromRef.current?.closest('[data-strip]');
    const both = () => { measure(); measureData(); };
    window.addEventListener('resize', both);
    // CAPTURE, because a scroll event does not bubble: this catches the strip's
    // own horizontal scroll AND the page scrolling INSIDE any card, both of
    // which move the boxes the wires are anchored to.
    strip?.addEventListener('scroll', both, true);
    return () => {
      window.removeEventListener('resize', both);
      strip?.removeEventListener('scroll', both, true);
    };
  }, [measure, measureData, active, connections.length]);

  /**
   * REVEAL WHAT IS SELECTED. A page is 844px in a 471px frame, so the module a
   * pick or a preview tap names is very often below the focus card's fold — the
   * box was painted on something nobody could see, and the wire ran off the
   * bottom edge of the card.
   *
   * It scrolls the phone's OWN page scroller and nothing else (`scrollWithin`,
   * never `scrollIntoView`: that walks every ancestor and would jump the studio
   * page). The scroller lives under `transform: scale(0.558)`, which the util
   * reads off the element rather than being told.
   *
   * After it moves, the wires are anchored to boxes that just changed — the
   * strip's capture-phase scroll listener redraws them for a USER scroll, but a
   * programmatic `scrollTop` write fires no event in every engine, so re-measure
   * here rather than leave a wire pointing at where the card used to be.
   */
  useLayoutEffect(() => {
    if (!revealId) return;
    if (!scrollNodeIntoView(fromRef.current, revealId)) return;
    measure();
    measureData();
  }, [revealId, shownPage, feed, measure, measureData]);

  const pick = useCallback((i) => {
    setPicked({ focus, index: i });
    // the front of the run is where the wire lands — show it
    try { fromRef.current?.closest('[data-strip]')?.scrollTo?.({ left: 0, behavior: 'smooth' }); } catch { /* jsdom */ }
  }, [focus]);

  if (!blueprint) return null;
  const wireColor = wired ? laneColor(wired.executor) : C.dim;

  return (
    <>
      <InspectStyles />

      {/* preview │ widget data — the app ends here, the diagram begins */}
      <Separator />

      <DataModelCard
        zone="Data Zone"
        blueprint={blueprint}
        focus={focus}
        cardRef={dataRef}
        rowRef={dataRowRef}
        dataMode={dataMode}
        feed={feed}
        live={live}
        onSelect={setDataZone}
        onPicked={takePick}
        renderers={renderers}
        focusItem={focusItemId}
        // …and what the page the focus card is drawing was OPENED with: a
        // detail-page module reads the row `page_args` names, so this is how
        // the data zone knows which row that is.
        pageArgs={pageArgs}
        onFocusModule={focusFromZone}
      />

      {/* widget data │ focus */}
      <Separator dashed />

      {/* THE FOCUS CARD — top-down of the focus, and the selection surface */}
      <PhoneCard
        testId="focus-self"
        zone="Focus Zone"
        framed={false}
        frameRef={fromRef}
        active={Boolean(focus)}
        accent={C.accent}
        title={focus
          ? <CardTitle id={focus} type={self?.type} level={levelOf(self?.type)} onJump={jumpTo} />
          : `${shownPage || 'page'} · no focus`}
        sub={focus
          ? <>{ownerType ? (
            <>owned by <code>{ownerId}</code> · <TypeBadge type={ownerType} level={levelOf(ownerType)} onJump={jumpTo} /> · </>
          ) : null}in focus · page <code>{shownPage}</code> — tap again to step out</>
          : <>page <code>{shownPage}</code> · tap a module to focus it{hint ? <> — try <code>{hint}</code></> : null}</>}
      >
        <div className="vi-focus-zone vi-focus-mark" style={{ position: 'absolute', inset: 0 }}>
          <NativeWidgetRuntime
            widgetId={widgetId}
            blueprint={blueprint}
            feed={feed}
            pageId={shownPage}
            // FOLLOW the preview's navigation, not just its page: an item detail
            // reached by tapping a row carries `page_args.item_id`, and without
            // it this same page renders its "no item selected" empty state.
            pageArgs={pageArgs}
            appearance={appearance}
            interaction={interaction}
            // NEVER A HOST, not even with nothing focused. This card looked
            // like the live app in that state and was wired like it too, so a
            // tap that reached the widget really did perform the host action —
            // a page open, a capture, a `closePage` that walked the whole strip
            // back to the entry page. The InspectLayer is meant to swallow
            // every tap, but "the diagram cannot act" must not rest on one
            // overlay never having a hole in it. The app still RENDERS: the
            // host is only ever consulted to DISPATCH a slot.
            host={null}
            // EVERY endpoint of the pick is boxed, not one of them: the boxes
            // are the wires' own targets (`boxedIds`), so each wire arrives at
            // an outlined node and no box is left over pointing somewhere else.
            inspect={focus
              ? { dim: self.dim, box: boxedIds }
              : (boxedIds.size ? { ...liveInspect, box: boxedIds } : liveInspect)}
          />
          <InspectLayer
            testId="focus-inspect-overlay"
            focus={focus}
            // the blueprint is what lets a tap resolve to a MODULE rather than
            // to whichever layout box happened to be innermost under the point
            blueprint={blueprint}
            onPick={focusFromZone}
            onEmpty={() => focusFromZone(null)}
          />
        </div>
      </PhoneCard>

      {/* focus │ connections */}
      <Separator dashed />

      {/* ONE CARD PER CONNECTION — the active one leads the run */}
      {ordered.map((v, i) => {
        const isActive = i === 0;
        // A host slot that opens one of OUR pages draws that page; every other
        // host slot has no UI of ours behind it and stays a bridge chip.
        const opens = v.kind === 'host' ? hostOpensPage(blueprint, v.slot) : null;
        const drawnPage = opens ? opens.pageId : (v.kind === 'host' ? null : v.page?.id || null);
        // …and a host slot with no page of ours may still open a SESSION, in
        // which case the bridge draws what that session receives.
        const session = v.kind === 'host' && !opens ? sessionFor(v) : null;
        return (
          <PhoneCard
            key={v.key}
            testId={`focus-card-${v.nodeId}`}
            // ONE title for the whole run, on the card that leads it
            zone={isActive ? 'Connections Zone' : null}
            framed={false}
            frameRef={isActive ? toRef : null}
            active={isActive}
            accent={laneColor(v.executor)}
            onClick={v.kind === 'host' ? () => pick(connections.indexOf(v)) : null}
            title={v.kind === 'host'
              ? <CardTitle id={v.nodeId} type={v.type} />
              : <CardTitle id={v.nodeId} type={v.type} level={levelOf(v.type)} onJump={jumpTo} />}
            sub={<>{v.caption}
              {opens
                ? <> · opens <code>{opens.pageId}</code></>
                : v.page && v.kind !== 'host' ? <> · page <code>{v.page.id}</code></> : null}</>}
          >
            {v.kind === 'host' && !opens ? (
              // The host has no page to draw. That is not the same as nothing
              // happening: a SESSION slot carries the prompt and the framing
              // the app dispatches, and the card draws that instead of the
              // "no UI" line. Everything else — `crudRemove`, `imageTap`, the
              // shell slots — really is an action with no content of its own.
              <div style={{
                position: 'absolute', inset: 0, background: '#f6f6f9',
                display: 'flex', flexDirection: 'column', alignItems: 'center',
                justifyContent: session ? 'flex-start' : 'center',
                gap: session ? 4 : 8, padding: session ? 12 : 20, textAlign: 'center',
              }}>
                <span style={{ fontSize: 13, fontWeight: 800, color: C.host }}>host bridge</span>
                <code style={{ fontSize: 12, color: C.text }}>{v.slot}</code>
                {session
                  ? <SessionCard node={session} />
                  : <span style={{ fontSize: 11, color: C.dim }}>no UI — the app performs it</span>}
              </div>
            ) : i < RENDERED ? (
              <div className="vi-focus-mark" style={{ position: 'absolute', inset: 0 }}>
                <NativeWidgetRuntime
                  widgetId={widgetId}
                  blueprint={blueprint}
                  feed={feed}
                  pageId={drawnPage}
                  // A host-opened page is opened ON A ROW — the whole point of
                  // drawing it — so it carries the args the host would push,
                  // never the preview's. Every other card: only the ones on the
                  // SAME page as the preview share its args; a connection on
                  // another page keeps its own defaults (item_id from data_view
                  // means nothing on index).
                  pageArgs={opens
                    ? openArgs(opens.argKey)
                    : (v.page?.id === shownPage ? pageArgs : null)}
                  appearance={appearance}
                  interaction={interaction}
                  // The host end has no module of ours to outline, so a
                  // host-opened page boxes the PAGE it opens — the card's whole
                  // subject — instead of an id (`host`) that marks nothing.
                  inspect={opens
                    ? { dim: NO_DIM, box: pageRootOf(opens.pageId) }
                    : { dim: v.dim, box: v.nodeId }}
                />
                {/* REORDER-ONLY: a connection card is not the app and not a
                    focus target. Any tap — on a module or on empty page — just
                    brings this card to the front so the wire redraws to it. No
                    host action fires; the focus never changes. */}
                <InspectLayer
                  testId={`focus-overlay-${v.nodeId}`}
                  onPick={() => pick(connections.indexOf(v))}
                  onEmpty={() => pick(connections.indexOf(v))}
                />
              </div>
            ) : (
              <div style={{
                position: 'absolute', inset: 0,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
                gap: 6, padding: 20, textAlign: 'center', color: C.dim, fontSize: 11.5,
              }}>
                <code style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{v.nodeId}</code>
                click to draw this page
              </div>
            )}
          </PhoneCard>
        );
      })}

      {focus && !connections.length ? (
        <div style={{ flex: '0 0 auto', alignSelf: 'center', fontSize: 12, color: C.dim, maxWidth: 220, lineHeight: 1.6 }}>
          This module has no connections — it is rendered, never wired.
        </div>
      ) : null}

      {/* THE DATA WIRES — the data zone's own connections, on the focus card's
          left. With a row picked they FAN OUT of it, one per place that row is
          displayed (and back to the writes it folded from); with nothing picked
          they CONVERGE on the focus, one per row that feeds it. Each carries
          its own label, and each that lands in the focus zone its own box. */}
      {dataWires?.lines?.length ? (
        <svg
          data-testid="focus-data-wire"
          width={dataWires.w} height={dataWires.h}
          style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 50, overflow: 'visible' }}
        >
          <defs>
            <marker id="viDataArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={C.data} />
            </marker>
          </defs>
          {dataWires.lines.map((l) => (
            // KEYED BY BOTH ENDS: a converging read has one target and many
            // starts, so the target alone stopped being a key the moment the
            // wires could point inward.
            <g key={l.key} data-wire-from={l.from} data-wire-target={l.target} data-wire-zone={l.zone}>
              {/* the label goes FIRST — its halo must never erase the line */}
              <text
                x={(l.x1 + 3 * (l.x1 + 30) + 3 * (l.x2 - 30) + l.x2) / 8}
                y={(l.y1 + 3 * l.y1 + 3 * l.y2 + l.y2) / 8 - 16}
                textAnchor="middle" fontSize="10.5" fill={C.data}
                fontFamily="ui-monospace, SFMono-Regular, monospace"
                stroke="#fff" strokeWidth="3.5" paintOrder="stroke"
              >{l.label}</text>
              <path
                data-testid="focus-data-wire-path"
                d={`M ${l.x1} ${l.y1} C ${l.x1 + 30} ${l.y1}, ${l.x2 - 30} ${l.y2}, ${l.x2} ${l.y2}`}
                fill="none" stroke={C.data} strokeWidth="1.8" strokeDasharray="5 4"
                markerEnd="url(#viDataArrow)"
              />
              <circle cx={l.x1} cy={l.y1} r="3" fill="#fff" stroke={C.data} strokeWidth="1.5" />
              <circle cx={l.x2} cy={l.y2} r="3" fill={C.data} stroke={C.data} strokeWidth="1.5" />
            </g>
          ))}
        </svg>
      ) : null}

      {/* THE WIRE — inside the strip's scroll content, above every card */}
      {wire && wired ? (
        <svg
          data-testid="focus-wire"
          data-dir={wired.dir}
          width={wire.w} height={wire.h}
          style={{ position: 'absolute', left: 0, top: 0, pointerEvents: 'none', zIndex: 50, overflow: 'visible' }}
        >
          <defs>
            <marker id="viFocusArrow" viewBox="0 0 10 10" refX="9" refY="5" markerWidth="7" markerHeight="7" orient="auto-start-reverse">
              <path d="M 0 0 L 10 5 L 0 10 z" fill={wireColor} />
            </marker>
          </defs>
          {/* the label goes FIRST — its halo must never erase the line */}
          <text
            x={(wire.x1 + 3 * (wire.x1 + 30) + 3 * (wire.x2 - 30) + wire.x2) / 8}
            y={(wire.y1 + 3 * wire.y1 + 3 * wire.y2 + wire.y2) / 8 - 16}
            textAnchor="middle" fontSize="10.5" fill={wireColor}
            fontFamily="ui-monospace, SFMono-Regular, monospace"
            stroke="#fff" strokeWidth="3.5" paintOrder="stroke"
          >{wired.signal} → {wired.slot}</text>
          <path
            data-testid="focus-wire-path"
            d={`M ${wire.x1} ${wire.y1} C ${wire.x1 + 30} ${wire.y1}, ${wire.x2 - 30} ${wire.y2}, ${wire.x2} ${wire.y2}`}
            fill="none" stroke={wireColor} strokeWidth="1.8"
            strokeDasharray={wired.mode === 'sync' ? undefined : '5 4'}
            markerEnd={wired.dir === 'out' ? 'url(#viFocusArrow)' : undefined}
            markerStart={wired.dir === 'in' ? 'url(#viFocusArrow)' : undefined}
          />
          <circle cx={wire.x1} cy={wire.y1} r="3" fill={wired.dir === 'out' ? wireColor : '#fff'} stroke={wireColor} strokeWidth="1.5" />
          <circle cx={wire.x2} cy={wire.y2} r="3" fill={wired.dir === 'in' ? wireColor : '#fff'} stroke={wireColor} strokeWidth="1.5" />
        </svg>
      ) : null}
    </>
  );
}
